import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

export interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: 'workspace_admin' | 'workspace_member';
  invited_by: string;
  invitation_token: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  accepted_by?: string;
  accepted_at?: string;
  created_at: string;
}

export interface InvitationRequest {
  emails: string[];
  role: 'workspace_admin' | 'workspace_member';
  message?: string;
}

export class InvitationService {
  /**
   * Check if workspace can send more invitations based on plan limits
   */
  static async canSendInvitations(workspaceId: string, additionalInvitations: number = 1): Promise<{
    canSend: boolean;
    currentUsers: number;
    maxUsers: number;
    pendingInvitations: number;
    planName: string;
  }> {
    try {
      // Get workspace limits and current usage
      const { data: limits, error } = await (supabase as any)
        .rpc('get_workspace_limits_with_usage', { workspace_uuid: workspaceId });

      if (error || !limits || limits.length === 0) {
        throw new Error('Failed to get workspace limits');
      }

      const limit = limits[0];
      const currentUsers = limit.current_users || 0;
      const maxUsers = limit.max_users || 0;
      const pendingInvitations = limit.pending_invitations || 0;

      // Check if we can add more users (including pending invitations)
      const totalInvitations = pendingInvitations + additionalInvitations;
      const canSend = maxUsers === -1 || (currentUsers + totalInvitations) <= maxUsers;

      return {
        canSend,
        currentUsers,
        maxUsers,
        pendingInvitations,
        planName: limit.plan_name
      };
    } catch (error) {
      // Return a safe default that prevents sending invitations
      return {
        canSend: false,
        currentUsers: 0,
        maxUsers: 0,
        pendingInvitations: 0,
        planName: 'unknown'
      };
    }
  }

  /**
   * Send invitations to multiple users
   */
  static async sendInvitations(
    workspaceId: string, 
    invitationRequest: InvitationRequest
  ): Promise<{ success: string[], failed: string[], error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check invitation limits before sending
      const limitCheck = await this.canSendInvitations(workspaceId, invitationRequest.emails.length);
      
      if (!limitCheck.canSend) {
        const errorMessage = limitCheck.maxUsers === -1 
          ? 'Unable to send invitations at this time'
          : `You've reached the maximum number of users for your ${limitCheck.planName} plan. You currently have ${limitCheck.currentUsers} active users and ${limitCheck.pendingInvitations} pending invitations. Consider upgrading your plan to invite more team members.`;
        
        return {
          success: [],
          failed: invitationRequest.emails,
          error: errorMessage
        };
      }

      const success: string[] = [];
      const failed: string[] = [];

      for (const email of invitationRequest.emails) {
        try {
          // No duplicate check here; assume form already filtered out duplicates
          // Create invitation
          const invitationToken = uuidv4();
          const expiresAt = new Date();
          expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

          const { data: invitation, error } = await (supabase as any)
            .from('workspace_invitations')
            .insert([{
              workspace_id: workspaceId,
              email: email.toLowerCase(),
              role: invitationRequest.role,
              invited_by: user.id,
              invitation_token: invitationToken,
              expires_at: expiresAt.toISOString(),
              status: 'pending'
            }])
            .select()
            .single();

          if (error) {
            failed.push(`${email} (database error)`);
            continue;
          }

          // Send invitation email
          await this.sendEmail(invitation.id, invitationRequest.message);
          success.push(email);

        } catch (error) {
          failed.push(`${email} (error)`);
        }
      }

      return { success, failed };
    } catch (error) {
      return {
        success: [],
        failed: invitationRequest.emails,
        error: 'An unexpected error occurred while sending invitations. Please try again.'
      };
    }
  }

  /**
   * Get pending invitations for a workspace
   */
  static async getPendingInvitations(workspaceId: string): Promise<WorkspaceInvitation[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('workspace_invitations')
        .select(`
          *,
          inviter:invited_by (
            id,
            display_name,
            email
          )
        `)
        .eq('workspace_id', workspaceId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Resend an invitation
   */
  static async resendInvitation(invitationId: string): Promise<boolean> {
    try {
      // Get the invitation
      const { data: invitation, error: fetchError } = await (supabase as any)
        .from('workspace_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (fetchError || !invitation) {
        throw new Error('Invitation not found');
      }

      // Update expiration date
      const newExpiresAt = new Date();
      newExpiresAt.setDate(newExpiresAt.getDate() + 7);

      const { error: updateError } = await (supabase as any)
        .from('workspace_invitations')
        .update({
          expires_at: newExpiresAt.toISOString()
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // Resend email
      await this.sendEmail(invitationId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Revoke an invitation
   */
  static async revokeInvitation(invitationId: string): Promise<boolean> {
    try {
      const { error } = await (supabase as any)
        .from('workspace_invitations')
        .update({
          status: 'revoked'
        })
        .eq('id', invitationId);

      if (error) throw error;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Send invitation email via Supabase Edge Function
   */
  private static async sendEmail(invitationId: string, customMessage?: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No authenticated session');
      }

      // Send the invitation email
      const { data, error } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          invitationId,
          customMessage
        }
      });

      if (error) {
        console.error('Error calling send-invitation-email function:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('Error sending invitation email:', error);
      throw error;
    }
  }

  /**
   * Get invitation by token
   */
  static async getInvitationByToken(token: string): Promise<WorkspaceInvitation | null> {
    try {
      const { data, error } = await (supabase as any)
        .from('workspace_invitations')
        .select(`
          *,
          workspace:workspace_id (
            name,
            slug
          ),
          inviter:invited_by (
            display_name,
            email
          )
        `)
        .eq('invitation_token', token)
        .single();

      if (error || !data) return null;
      return data;
    } catch (error) {
      console.error('Error fetching invitation:', error);
      return null;
    }
  }

  /**
   * Accept invitation by token
   */
  static async acceptInvitation(token: string): Promise<{ success: boolean; workspace?: any; error?: string }> {
    try {
      // Get invitation
      const { data: invitation, error: invitationError } = await (supabase as any)
        .from('workspace_invitations')
        .select('*')
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .single();

      if (invitationError || !invitation) {
        return { success: false, error: 'Invalid or expired invitation' };
      }

      // Check if expired
      if (new Date(invitation.expires_at) < new Date()) {
        return { success: false, error: 'Invitation has expired' };
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user already has access to this workspace
      const { data: existingMembership } = await (supabase as any)
        .from('workspace_memberships')
        .select('id')
        .eq('workspace_id', invitation.workspace_id)
        .eq('user_id', user.id)
        .single();

      if (existingMembership) {
        return { success: false, error: 'You already have access to this workspace' };
      }

      // Check if user has any existing workspace memberships to determine if this should be default
      const { data: existingMemberships } = await (supabase as any)
        .from('workspace_memberships')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'active');

      const isFirstWorkspace = !existingMemberships || existingMemberships.length === 0;

      // Create workspace membership
      const { error: membershipError } = await (supabase as any)
        .from('workspace_memberships')
        .insert([{
          workspace_id: invitation.workspace_id,
          user_id: user.id,
          role: invitation.role,
          invited_by: invitation.invited_by,
          invited_at: invitation.created_at,
          joined_at: new Date().toISOString(),
          status: 'active',
          is_default: isFirstWorkspace // Set as default if this is user's first workspace
        }]);

      if (membershipError) {
        console.error('Error creating membership:', membershipError);
        return { success: false, error: 'Failed to join workspace' };
      }

      // Update invitation status
      await (supabase as any)
        .from('workspace_invitations')
        .update({
          status: 'accepted',
          accepted_by: user.id,
          accepted_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      // Get workspace details
      const { data: workspace } = await (supabase as any)
        .from('watt_workspaces')
        .select('*')
        .eq('id', invitation.workspace_id)
        .single();

      return { success: true, workspace };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      return { success: false, error: 'Failed to accept invitation' };
    }
  }
} 